/**
 * Crews Duck
 *
 * Manages crews/linkups data from Supabase.
 * Handles joining, leaving, creating crews and fetching crew members.
 */

import { createAction, createSlice } from '@reduxjs/toolkit';
import { storableError } from '../util/errors';
import * as log from '../util/log';
import { apiBaseUrl } from '../util/api';
import { transformCrewData, transformCrewList } from '../util/crewData';

// ================ Action types ================ //

export const FETCH_USER_CREWS_REQUEST = 'app/crews/FETCH_USER_CREWS_REQUEST';
export const FETCH_USER_CREWS_SUCCESS = 'app/crews/FETCH_USER_CREWS_SUCCESS';
export const FETCH_USER_CREWS_ERROR = 'app/crews/FETCH_USER_CREWS_ERROR';

export const FETCH_CREW_DETAILS_REQUEST = 'app/crews/FETCH_CREW_DETAILS_REQUEST';
export const FETCH_CREW_DETAILS_SUCCESS = 'app/crews/FETCH_CREW_DETAILS_SUCCESS';
export const FETCH_CREW_DETAILS_ERROR = 'app/crews/FETCH_CREW_DETAILS_ERROR';

export const CREATE_CREW_REQUEST = 'app/crews/CREATE_CREW_REQUEST';
export const CREATE_CREW_SUCCESS = 'app/crews/CREATE_CREW_SUCCESS';
export const CREATE_CREW_ERROR = 'app/crews/CREATE_CREW_ERROR';

export const JOIN_CREW_REQUEST = 'app/crews/JOIN_CREW_REQUEST';
export const JOIN_CREW_SUCCESS = 'app/crews/JOIN_CREW_SUCCESS';
export const JOIN_CREW_ERROR = 'app/crews/JOIN_CREW_ERROR';

export const LEAVE_CREW_REQUEST = 'app/crews/LEAVE_CREW_REQUEST';
export const LEAVE_CREW_SUCCESS = 'app/crews/LEAVE_CREW_SUCCESS';
export const LEAVE_CREW_ERROR = 'app/crews/LEAVE_CREW_ERROR';

export const SEARCH_CREWS_REQUEST = 'app/crews/SEARCH_CREWS_REQUEST';
export const SEARCH_CREWS_SUCCESS = 'app/crews/SEARCH_CREWS_SUCCESS';
export const SEARCH_CREWS_ERROR = 'app/crews/SEARCH_CREWS_ERROR';

export const FETCH_FEATURED_CREWS_REQUEST = 'app/crews/FETCH_FEATURED_CREWS_REQUEST';
export const FETCH_FEATURED_CREWS_SUCCESS = 'app/crews/FETCH_FEATURED_CREWS_SUCCESS';
export const FETCH_FEATURED_CREWS_ERROR = 'app/crews/FETCH_FEATURED_CREWS_ERROR';

export const FETCH_USER_MEMBERSHIPS_REQUEST = 'app/crews/FETCH_USER_MEMBERSHIPS_REQUEST';
export const FETCH_USER_MEMBERSHIPS_SUCCESS = 'app/crews/FETCH_USER_MEMBERSHIPS_SUCCESS';
export const FETCH_USER_MEMBERSHIPS_ERROR = 'app/crews/FETCH_USER_MEMBERSHIPS_ERROR';

export const CLEAR_CREWS = 'app/crews/CLEAR_CREWS';

// ================ Reducer ================ //

export const initialState = {
  userCrews: [],
  featuredCrews: [],
  crewDetails: {},
  searchResults: [],
  crewCount: 0,
  // User memberships
  userMemberships: [],
  fetchUserCrewsInProgress: false,
  fetchUserCrewsError: null,
  userCrewsLastFetched: null,
  userCrewsUserId: null,
  fetchCrewDetailsInProgress: false,
  fetchCrewDetailsError: null,
  crewDetailsFetchedAt: {},
  createCrewInProgress: false,
  createCrewError: null,
  joinCrewInProgress: false,
  joinCrewError: null,
  leaveCrewInProgress: false,
  leaveCrewError: null,
  searchCrewsInProgress: false,
  searchCrewsError: null,
  fetchFeaturedCrewsInProgress: false,
  fetchFeaturedCrewsError: null,
  featuredCrewsLastFetched: null,
  featuredCrewsQueryKey: null,
  fetchUserMembershipsInProgress: false,
  fetchUserMembershipsError: null,
  userMembershipsLastFetched: null,
  userMembershipsUserId: null,
};

const fetchUserCrewsRequestAction = createAction(FETCH_USER_CREWS_REQUEST);
const fetchUserCrewsSuccessAction = createAction(FETCH_USER_CREWS_SUCCESS);
const fetchUserCrewsErrorAction = createAction(FETCH_USER_CREWS_ERROR);

const fetchCrewDetailsRequestAction = createAction(FETCH_CREW_DETAILS_REQUEST);
const fetchCrewDetailsSuccessAction = createAction(FETCH_CREW_DETAILS_SUCCESS);
const fetchCrewDetailsErrorAction = createAction(FETCH_CREW_DETAILS_ERROR);

const createCrewRequestAction = createAction(CREATE_CREW_REQUEST);
const createCrewSuccessAction = createAction(CREATE_CREW_SUCCESS);
const createCrewErrorAction = createAction(CREATE_CREW_ERROR);

const joinCrewRequestAction = createAction(JOIN_CREW_REQUEST);
const joinCrewSuccessAction = createAction(JOIN_CREW_SUCCESS);
const joinCrewErrorAction = createAction(JOIN_CREW_ERROR);

const leaveCrewRequestAction = createAction(LEAVE_CREW_REQUEST);
const leaveCrewSuccessAction = createAction(LEAVE_CREW_SUCCESS);
const leaveCrewErrorAction = createAction(LEAVE_CREW_ERROR);

const searchCrewsRequestAction = createAction(SEARCH_CREWS_REQUEST);
const searchCrewsSuccessAction = createAction(SEARCH_CREWS_SUCCESS);
const searchCrewsErrorAction = createAction(SEARCH_CREWS_ERROR);

const fetchFeaturedCrewsRequestAction = createAction(FETCH_FEATURED_CREWS_REQUEST);
const fetchFeaturedCrewsSuccessAction = createAction(FETCH_FEATURED_CREWS_SUCCESS);
const fetchFeaturedCrewsErrorAction = createAction(FETCH_FEATURED_CREWS_ERROR);

const fetchUserMembershipsRequestAction = createAction(FETCH_USER_MEMBERSHIPS_REQUEST);
const fetchUserMembershipsSuccessAction = createAction(FETCH_USER_MEMBERSHIPS_SUCCESS);
const fetchUserMembershipsErrorAction = createAction(FETCH_USER_MEMBERSHIPS_ERROR);

const clearCrewsAction = createAction(CLEAR_CREWS);

const crewsSlice = createSlice({
  name: 'crews',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchUserCrewsRequestAction, state => {
        state.fetchUserCrewsInProgress = true;
        state.fetchUserCrewsError = null;
      })
      .addCase(fetchUserCrewsSuccessAction, (state, action) => {
        state.userCrews = action.payload.crews;
        state.crewCount = action.payload.crews.length;
        state.fetchUserCrewsInProgress = false;
        state.userCrewsLastFetched = Date.now();
        state.userCrewsUserId = action.payload.userId || null;
      })
      .addCase(fetchUserCrewsErrorAction, (state, action) => {
        state.fetchUserCrewsInProgress = false;
        state.fetchUserCrewsError = action.payload;
      })
      .addCase(fetchCrewDetailsRequestAction, state => {
        state.fetchCrewDetailsInProgress = true;
        state.fetchCrewDetailsError = null;
      })
      .addCase(fetchCrewDetailsSuccessAction, (state, action) => {
        state.crewDetails[action.payload.crew.id] = action.payload.crew;
        state.crewDetailsFetchedAt[action.payload.crew.id] = Date.now();
        state.fetchCrewDetailsInProgress = false;
      })
      .addCase(fetchCrewDetailsErrorAction, (state, action) => {
        state.fetchCrewDetailsInProgress = false;
        state.fetchCrewDetailsError = action.payload;
      })
      .addCase(createCrewRequestAction, state => {
        state.createCrewInProgress = true;
        state.createCrewError = null;
      })
      .addCase(createCrewSuccessAction, (state, action) => {
        state.userCrews.push(action.payload.crew);
        // Also add to featuredCrews so CrewPage can find it immediately after navigation
        if (action.payload.crew) {
          state.featuredCrews = [action.payload.crew, ...(state.featuredCrews || [])];
        }
        state.crewCount += 1;
        state.createCrewInProgress = false;
      })
      .addCase(createCrewErrorAction, (state, action) => {
        state.createCrewInProgress = false;
        state.createCrewError = action.payload;
      })
      .addCase(joinCrewRequestAction, state => {
        state.joinCrewInProgress = true;
        state.joinCrewError = null;
      })
      .addCase(joinCrewSuccessAction, (state, action) => {
        state.userCrews.push(action.payload.crew);
        state.crewCount += 1;
        state.joinCrewInProgress = false;
      })
      .addCase(joinCrewErrorAction, (state, action) => {
        state.joinCrewInProgress = false;
        state.joinCrewError = action.payload;
      })
      .addCase(leaveCrewRequestAction, state => {
        state.leaveCrewInProgress = true;
        state.leaveCrewError = null;
      })
      .addCase(leaveCrewSuccessAction, (state, action) => {
        state.userCrews = state.userCrews.filter(crew => crew.id !== action.payload.crewId);
        state.crewCount = Math.max(0, state.crewCount - 1);
        state.leaveCrewInProgress = false;
      })
      .addCase(leaveCrewErrorAction, (state, action) => {
        state.leaveCrewInProgress = false;
        state.leaveCrewError = action.payload;
      })
      .addCase(searchCrewsRequestAction, state => {
        state.searchCrewsInProgress = true;
        state.searchCrewsError = null;
      })
      .addCase(searchCrewsSuccessAction, (state, action) => {
        state.searchResults = action.payload.crews;
        state.searchCrewsInProgress = false;
      })
      .addCase(searchCrewsErrorAction, (state, action) => {
        state.searchCrewsInProgress = false;
        state.searchCrewsError = action.payload;
      })
      .addCase(fetchFeaturedCrewsRequestAction, state => {
        state.fetchFeaturedCrewsInProgress = true;
        state.fetchFeaturedCrewsError = null;
      })
      .addCase(fetchFeaturedCrewsSuccessAction, (state, action) => {
        state.featuredCrews = action.payload.crews;
        state.fetchFeaturedCrewsInProgress = false;
        state.featuredCrewsLastFetched = Date.now();
        state.featuredCrewsQueryKey = action.payload.queryKey;
      })
      .addCase(fetchFeaturedCrewsErrorAction, (state, action) => {
        state.fetchFeaturedCrewsInProgress = false;
        state.fetchFeaturedCrewsError = action.payload;
      })
      .addCase(fetchUserMembershipsRequestAction, state => {
        state.fetchUserMembershipsInProgress = true;
        state.fetchUserMembershipsError = null;
      })
      .addCase(fetchUserMembershipsSuccessAction, (state, action) => {
        state.userMemberships = action.payload.memberships;
        state.fetchUserMembershipsInProgress = false;
        state.userMembershipsLastFetched = Date.now();
        state.userMembershipsUserId = action.payload.userId;
      })
      .addCase(fetchUserMembershipsErrorAction, (state, action) => {
        state.fetchUserMembershipsInProgress = false;
        state.fetchUserMembershipsError = action.payload;
      })
      .addCase(clearCrewsAction, () => initialState);
  },
});

export default crewsSlice.reducer;

// ================ Selectors ================ //

// Raw data selectors (returns data as stored in Redux)
export const getUserCrews = state => state.crews?.userCrews || [];
export const getFeaturedCrews = state => state.crews?.featuredCrews || [];
export const getCrewCount = state => state.crews?.crewCount || 0;
export const getCrewDetails = (state, crewId) => state.crews?.crewDetails[crewId] || null;
export const getSearchResults = state => state.crews?.searchResults || [];

// Formatted data selectors (returns transformed data for components)
export const getFormattedUserCrews = state => {
  const crews = getUserCrews(state);
  return transformCrewList(crews);
};

export const getFormattedFeaturedCrews = state => {
  const crews = getFeaturedCrews(state);
  return transformCrewList(crews);
};

export const getFormattedCrewDetails = (state, crewId) => {
  const crew = getCrewDetails(state, crewId);
  return transformCrewData(crew, { includeFullDetails: true });
};

export const getFormattedSearchResults = state => {
  const crews = getSearchResults(state);
  return transformCrewList(crews);
};

// Loading and error state selectors
export const isCrewActionInProgress = state =>
  !!(
    state.crews?.fetchUserCrewsInProgress ||
    state.crews?.joinCrewInProgress ||
    state.crews?.leaveCrewInProgress ||
    state.crews?.createCrewInProgress ||
    state.crews?.fetchFeaturedCrewsInProgress
  );

export const isFetchingFeaturedCrews = state => state.crews?.fetchFeaturedCrewsInProgress || false;

export const getFeaturedCrewsError = state => state.crews?.fetchFeaturedCrewsError || null;

// Membership selectors
export const getUserMemberships = state => state.crews?.userMemberships || [];
export const getFormattedUserMemberships = state => {
  const memberships = getUserMemberships(state);
  return memberships.map(membership => ({
    ...membership,
    // Add any formatting needed for memberships
    formattedCreatedAt: new Date(membership.created_at).toLocaleDateString(),
  }));
};
export const isFetchingUserMemberships = state =>
  state.crews?.fetchUserMembershipsInProgress || false;
export const getUserMembershipsError = state => state.crews?.fetchUserMembershipsError || null;

// ================ Action creators ================ //

const fetchUserCrewsRequest = () => fetchUserCrewsRequestAction();
const fetchUserCrewsSuccess = crews => fetchUserCrewsSuccessAction({ crews });
const fetchUserCrewsError = error => fetchUserCrewsErrorAction(error);

const fetchCrewDetailsRequest = () => fetchCrewDetailsRequestAction();
const fetchCrewDetailsSuccess = crew => fetchCrewDetailsSuccessAction({ crew });
const fetchCrewDetailsError = error => fetchCrewDetailsErrorAction(error);

const createCrewRequest = () => createCrewRequestAction();
const createCrewSuccess = crew => createCrewSuccessAction({ crew });
const createCrewError = error => createCrewErrorAction(error);

const joinCrewRequest = () => joinCrewRequestAction();
const joinCrewSuccess = crew => joinCrewSuccessAction({ crew });
const joinCrewError = error => joinCrewErrorAction(error);

const leaveCrewRequest = () => leaveCrewRequestAction();
const leaveCrewSuccess = crewId => leaveCrewSuccessAction({ crewId });
const leaveCrewError = error => leaveCrewErrorAction(error);

const searchCrewsRequest = () => searchCrewsRequestAction();
const searchCrewsSuccess = crews => searchCrewsSuccessAction({ crews });
const searchCrewsError = error => searchCrewsErrorAction(error);

const fetchFeaturedCrewsRequest = () => fetchFeaturedCrewsRequestAction();
const fetchFeaturedCrewsSuccess = (crews, queryKey) =>
  fetchFeaturedCrewsSuccessAction({ crews, queryKey });
const fetchFeaturedCrewsError = error => fetchFeaturedCrewsErrorAction(error);

const fetchUserMembershipsRequest = () => fetchUserMembershipsRequestAction();
const fetchUserMembershipsSuccess = (memberships, userId) =>
  fetchUserMembershipsSuccessAction({ memberships, userId });
const fetchUserMembershipsError = error => fetchUserMembershipsErrorAction(error);

export const clearCrews = () => clearCrewsAction();

const FEATURED_CREWS_CACHE_DURATION = 30 * 1000; // 30 seconds — keep fresh so live LinkUp/Bid counts update
const USER_CREWS_CACHE_DURATION = 30 * 1000;
const CREW_DETAILS_CACHE_DURATION = 60 * 1000;
const USER_MEMBERSHIPS_CACHE_DURATION = 30 * 1000;
const SEARCH_CREWS_CACHE_DURATION = 60 * 1000;
const featuredCrewsRequests = new Map();
const userCrewsRequests = new Map();
const crewDetailsRequests = new Map();
const userMembershipRequests = new Map();
const searchCrewsRequests = new Map();
const searchCrewsCache = new Map();

const createQueryKey = options => {
  const entries = Object.entries(options || {}).filter(
    ([, value]) => value !== undefined && value !== null && value !== ''
  );
  entries.sort(([a], [b]) => a.localeCompare(b));
  return JSON.stringify(entries);
};

// ================ Thunks ================ //

/**
 * Fetch crews the current user is a member of
 */
export const fetchUserCrews = (fetchOptions = {}) => (dispatch, getState) => {
  const { currentUser } = getState().user;
  const { force = false } = fetchOptions;

  // Check if user is authenticated with proper ID
  if (!currentUser || !currentUser.id || !currentUser.id.uuid) {
    return Promise.resolve([]);
  }

  const userId = currentUser.id.uuid;
  const state = getState();
  const hasFreshCache =
    state.crews?.userCrewsUserId === userId &&
    state.crews?.userCrewsLastFetched &&
    Date.now() - state.crews.userCrewsLastFetched < USER_CREWS_CACHE_DURATION &&
    Array.isArray(state.crews?.userCrews);

  if (!force && hasFreshCache) {
    return Promise.resolve(state.crews.userCrews);
  }

  if (!force && userCrewsRequests.has(userId)) {
    return userCrewsRequests.get(userId);
  }

  dispatch(fetchUserCrewsRequest());

  const requestPromise = fetch(`${apiBaseUrl()}/api/supabase/crews/my-crews`, {
    method: 'GET',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  })
    .then(res => {
      if (!res.ok) {
        return res.json().then(data => {
          throw new Error(data.message || 'Failed to fetch crews');
        });
      }
      return res.json();
    })
    .then(data => {
      dispatch(fetchUserCrewsSuccessAction({ crews: data.crews || [], userId }));
      return data.crews;
    })
    .catch(e => {
      log.error(e, 'fetch-user-crews-failed');
      dispatch(fetchUserCrewsError(storableError(e)));
      throw e;
    })
    .finally(() => {
      userCrewsRequests.delete(userId);
    });

  userCrewsRequests.set(userId, requestPromise);
  return requestPromise;
};

/**
 * Fetch details for a specific crew
 */
export const fetchCrewDetails = (crewId, fetchOptions = {}) => (dispatch, getState) => {
  const { force = false } = fetchOptions;
  const crewIdKey = String(crewId || '');

  if (!crewIdKey) {
    return Promise.resolve(null);
  }

  const state = getState();
  const cachedCrew = state.crews?.crewDetails?.[crewIdKey];
  const fetchedAt = state.crews?.crewDetailsFetchedAt?.[crewIdKey];
  const hasFreshCache =
    cachedCrew && fetchedAt && Date.now() - fetchedAt < CREW_DETAILS_CACHE_DURATION;

  if (!force && hasFreshCache) {
    return Promise.resolve(cachedCrew);
  }

  if (!force && crewDetailsRequests.has(crewIdKey)) {
    return crewDetailsRequests.get(crewIdKey);
  }

  dispatch(fetchCrewDetailsRequest());

  const requestPromise = fetch(`${apiBaseUrl()}/api/supabase/crews/${crewIdKey}`, {
    method: 'GET',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  })
    .then(res => {
      if (!res.ok) {
        return res.json().then(data => {
          throw new Error(data.message || 'Failed to fetch crew details');
        });
      }
      return res.json();
    })
    .then(data => {
      dispatch(fetchCrewDetailsSuccess(data.crew));
      return data.crew;
    })
    .catch(e => {
      log.error(e, 'fetch-crew-details-failed');
      dispatch(fetchCrewDetailsError(storableError(e)));
      throw e;
    })
    .finally(() => {
      crewDetailsRequests.delete(crewIdKey);
    });

  crewDetailsRequests.set(crewIdKey, requestPromise);
  return requestPromise;
};

/**
 * Create a new crew
 */
export const createCrew = crewData => (dispatch, getState) => {
  const { currentUser } = getState().user;

  if (!currentUser) {
    return Promise.reject(new Error('No authenticated user'));
  }

  dispatch(createCrewRequest());

  return fetch(`${apiBaseUrl()}/api/supabase/crews`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(crewData),
  })
    .then(res => {
      if (!res.ok) {
        return res.json().then(data => {
          console.error('❌ Crew creation API error:', {
            status: res.status,
            statusText: res.statusText,
            data: data,
          });
          const error = new Error(
            data.moderation_unavailable
              ? 'Content moderation is temporarily unavailable. Your crew was not created. Please try again later.'
              : data.message || data.error || 'Failed to create crew'
          );
          error.status = res.status;
          error.data = data;
          throw error;
        });
      }
      return res.json();
    })
    .then(data => {
      // Handle moderation rejection (HTTP 200 but success: false)
      if (data.moderation_rejected) {
        dispatch(createCrewError({ message: data.message }));
        return data; // Return full response so component can check moderation_rejected
      }

      dispatch(createCrewSuccess(data.crew));

      // Refresh user memberships after creating crew (creator is automatically a member)
      if (currentUser.id && currentUser.id.uuid) {
        dispatch(fetchUserMemberships(currentUser.id.uuid, { force: true }));
      }

      // Refresh the full featured crews list so CrewPage has the new crew
      dispatch(fetchFeaturedCrews({ sortBy: 'recent', limit: 100 })).catch(() => {});

      return data;
    })
    .catch(e => {
      log.error(e, 'create-crew-failed');
      dispatch(createCrewError(storableError(e)));
      throw e;
    });
};

/**
 * Join an existing crew
 */
export const joinCrew = crewId => (dispatch, getState) => {
  const { currentUser } = getState().user;

  if (!currentUser || !currentUser.id || !currentUser.id.uuid) {
    return Promise.reject(new Error('No authenticated user'));
  }

  dispatch(joinCrewRequest());

  const userId = currentUser.id.uuid;

  return fetch(`${apiBaseUrl()}/api/supabase/crews/${crewId}/join`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId }),
  })
    .then(res => {
      if (!res.ok) {
        return res.json().then(data => {
          throw new Error(data.message || 'Failed to join crew');
        });
      }
      return res.json();
    })
    .then(data => {
      dispatch(joinCrewSuccess(data.crew));

      // Refresh user memberships after joining
      dispatch(fetchUserMemberships(userId, { force: true }));

      return data;
    })
    .catch(e => {
      log.error(e, 'join-crew-failed');
      dispatch(joinCrewError(storableError(e)));
      throw e;
    });
};

/**
 * Leave a crew
 */
export const leaveCrew = crewId => (dispatch, getState) => {
  const { currentUser } = getState().user;

  if (!currentUser || !currentUser.id || !currentUser.id.uuid) {
    return Promise.reject(new Error('No authenticated user'));
  }

  dispatch(leaveCrewRequest());

  const userId = currentUser.id.uuid;

  return fetch(`${apiBaseUrl()}/api/supabase/crews/${crewId}/leave`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId }),
  })
    .then(res => {
      if (!res.ok) {
        return res.json().then(data => {
          throw new Error(data.message || 'Failed to leave crew');
        });
      }
      return res.json();
    })
    .then(data => {
      dispatch(leaveCrewSuccess(crewId));

      // Refresh user memberships after leaving
      dispatch(fetchUserMemberships(userId, { force: true }));

      return data;
    })
    .catch(e => {
      log.error(e, 'leave-crew-failed');
      dispatch(leaveCrewError(storableError(e)));
      throw e;
    });
};

/**
 * Search for crews
 */
export const searchCrews = (query = '', filters = {}) => dispatch => {
  // Empty query will fetch all active crews from backend
  const params = new URLSearchParams({ q: query, ...filters });
  const queryKey = params.toString();
  const cachedEntry = searchCrewsCache.get(queryKey);

  if (
    cachedEntry &&
    Array.isArray(cachedEntry.crews) &&
    Date.now() - cachedEntry.timestamp < SEARCH_CREWS_CACHE_DURATION
  ) {
    dispatch(searchCrewsSuccess(cachedEntry.crews));
    return Promise.resolve(cachedEntry.crews);
  }

  if (searchCrewsRequests.has(queryKey)) {
    return searchCrewsRequests.get(queryKey);
  }

  dispatch(searchCrewsRequest());

  const requestPromise = fetch(`${apiBaseUrl()}/api/supabase/crews/search?${params}`, {
    method: 'GET',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  })
    .then(res => {
      if (!res.ok) {
        return res.json().then(data => {
          throw new Error(data.message || 'Failed to search crews');
        });
      }
      return res.json();
    })
    .then(data => {
      const crews = data.crews || [];
      searchCrewsCache.set(queryKey, { crews, timestamp: Date.now() });
      dispatch(searchCrewsSuccess(crews));
      return crews;
    })
    .catch(e => {
      log.error(e, 'search-crews-failed');
      dispatch(searchCrewsError(storableError(e)));
      throw e;
    })
    .finally(() => {
      searchCrewsRequests.delete(queryKey);
    });

  searchCrewsRequests.set(queryKey, requestPromise);
  return requestPromise;
};

/**
 * Fetch featured/discover crews for the discover page
 * Optionally filter by category or sort by various criteria
 */
export const fetchFeaturedCrews = (options = {}, fetchOptions = {}) => (dispatch, getState) => {
  const { force = false } = fetchOptions;
  const state = getState();
  const queryKey = createQueryKey(options);
  const isSameQuery = state.crews?.featuredCrewsQueryKey === queryKey;
  const lastFetched = state.crews?.featuredCrewsLastFetched;
  const hasFreshCache =
    isSameQuery &&
    lastFetched &&
    Date.now() - lastFetched < FEATURED_CREWS_CACHE_DURATION &&
    Array.isArray(state.crews?.featuredCrews);

  if (!force && hasFreshCache) {
    return Promise.resolve(state.crews.featuredCrews);
  }

  if (!force && featuredCrewsRequests.has(queryKey)) {
    return featuredCrewsRequests.get(queryKey);
  }

  dispatch(fetchFeaturedCrewsRequest());

  const { category, sortBy, limit } = options;
  const params = new URLSearchParams();

  if (category) params.append('category', category);
  if (sortBy) params.append('sort', sortBy);
  if (limit) params.append('limit', limit);

  const queryString = params.toString();
  const url = `${apiBaseUrl()}/api/supabase/crews/discover${queryString ? `?${queryString}` : ''}`;

  const requestPromise = fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  })
    .then(res => {
      if (!res.ok) {
        return res.json().then(data => {
          throw new Error(data.message || 'Failed to fetch featured crews');
        });
      }
      return res.json();
    })
    .then(data => {
      dispatch(fetchFeaturedCrewsSuccess(data.crews || [], queryKey));
      return data.crews;
    })
    .catch(e => {
      log.error(e, 'fetch-featured-crews-failed');
      dispatch(fetchFeaturedCrewsError(storableError(e)));
      throw e;
    })
    .finally(() => {
      featuredCrewsRequests.delete(queryKey);
    });

  featuredCrewsRequests.set(queryKey, requestPromise);
  return requestPromise;
};

/**
 * Fetch user memberships from CrewMembership table
 */
export const fetchUserMemberships = (userId, fetchOptions = {}) => (dispatch, getState) => {
  // If no userId provided, silently return empty array
  if (!userId) {
    return Promise.resolve([]);
  }

  const { force = false } = fetchOptions;
  const state = getState();
  const sameUser = state.crews?.userMembershipsUserId === userId;
  const lastFetched = state.crews?.userMembershipsLastFetched;
  const hasFreshCache =
    sameUser &&
    lastFetched &&
    Date.now() - lastFetched < USER_MEMBERSHIPS_CACHE_DURATION &&
    Array.isArray(state.crews?.userMemberships);

  if (!force && hasFreshCache) {
    return Promise.resolve(state.crews.userMemberships);
  }

  if (!force && userMembershipRequests.has(userId)) {
    return userMembershipRequests.get(userId);
  }

  dispatch(fetchUserMembershipsRequest());

  const requestPromise = fetch(`${apiBaseUrl()}/api/supabase/crews/memberships?user_id=${userId}`, {
    method: 'GET',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  })
    .then(res => {
      if (!res.ok) {
        return res.json().then(data => {
          throw new Error(data.message || 'Failed to fetch memberships');
        });
      }
      return res.json();
    })
    .then(data => {
      dispatch(fetchUserMembershipsSuccess(data.memberships || [], userId));
      return data.memberships;
    })
    .catch(e => {
      log.error(e, 'fetch-user-memberships-failed');
      dispatch(fetchUserMembershipsError(storableError(e)));
      throw e;
    })
    .finally(() => {
      userMembershipRequests.delete(userId);
    });

  userMembershipRequests.set(userId, requestPromise);
  return requestPromise;
};
